import type {
  CreateWorkspaceRequest,
  ProviderDto,
  Role,
  WorkspaceDocumentDto,
  WorkspaceDocumentStatus,
  WorkspaceDto,
  WorkspaceEmbedding,
  WorkspaceSearchResult,
} from '@agent-platform/shared';
import {
  formatBytes,
  hasRole,
  WORKSPACE_EMBEDDING_DIMS,
  WORKSPACE_EMBEDDING_PROVIDER_TYPES,
} from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardPaste, RefreshCw, Trash2, Upload } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { ListSearch, NoMatches, useListSearch } from '@/components/list-search';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { api, errorMessage } from '@/lib/api';
import { uploadFile } from '@/lib/files';
import { formatDate } from '@/lib/format';
import { usePaged } from '@/lib/queries';

const EMBEDDING_TYPES: readonly string[] = WORKSPACE_EMBEDDING_PROVIDER_TYPES;
const ACCEPT = '.txt,.md,.markdown,.csv,.json,.html,.htm,.pdf';

/** Embedding choice while editing: keyword-only, or a connection + model + size. */
interface EmbeddingForm {
  vector: boolean;
  providerConnectionId: string;
  model: string;
  dims: string;
}

const emptyEmbedding = (e: WorkspaceEmbedding | null = null): EmbeddingForm => ({
  vector: e !== null,
  providerConnectionId: e?.providerConnectionId ?? '',
  model: e?.model ?? '',
  dims: String(e?.dims ?? 1536),
});

const toEmbedding = (f: EmbeddingForm): WorkspaceEmbedding | null =>
  f.vector
    ? {
        providerConnectionId: f.providerConnectionId,
        model: f.model.trim(),
        dims: Number(f.dims) as WorkspaceEmbedding['dims'],
      }
    : null;

function useEmbeddingProviders(tenantId: string) {
  const providers = usePaged<ProviderDto>(['providers', tenantId], '/providers');
  return (providers.data?.pages.flatMap((p) => p.items) ?? []).filter((p) =>
    EMBEDDING_TYPES.includes(p.type),
  );
}

function EmbeddingFields({
  idPrefix,
  value,
  onChange,
  providers,
}: {
  idPrefix: string;
  value: EmbeddingForm;
  onChange: (next: EmbeddingForm) => void;
  providers: ProviderDto[];
}) {
  const set = (patch: Partial<EmbeddingForm>) => onChange({ ...value, ...patch });
  return (
    <fieldset className="grid gap-3">
      <legend className="mb-1 text-sm font-medium">Search mode</legend>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="radio"
          name={`${idPrefix}-mode`}
          checked={!value.vector}
          onChange={() => set({ vector: false })}
        />
        Keyword only
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="radio"
          name={`${idPrefix}-mode`}
          checked={value.vector}
          onChange={() => set({ vector: true })}
        />
        Keyword + vector
      </label>
      {value.vector &&
        (providers.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Add an OpenAI, Google or OpenAI-compatible connection to enable vector search.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
            <div className="grid gap-1.5">
              <Label htmlFor={`${idPrefix}-provider`}>Embedding connection</Label>
              <Select
                value={value.providerConnectionId}
                onValueChange={(v) => v && set({ providerConnectionId: v })}
              >
                <SelectTrigger id={`${idPrefix}-provider`} className="w-full">
                  <SelectValue placeholder="Choose a connection" />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor={`${idPrefix}-model`}>Embedding model</Label>
              <Input
                id={`${idPrefix}-model`}
                className="font-mono"
                value={value.model}
                onChange={(e) => set({ model: e.target.value })}
                placeholder="text-embedding-3-small"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor={`${idPrefix}-dims`}>Dimensions</Label>
              <Select value={value.dims} onValueChange={(v) => v && set({ dims: v })}>
                <SelectTrigger id={`${idPrefix}-dims`} className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WORKSPACE_EMBEDDING_DIMS.map((d) => (
                    <SelectItem key={d} value={String(d)}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
    </fieldset>
  );
}

function NewWorkspaceDialog({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const providers = useEmbeddingProviders(tenantId);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [embedding, setEmbedding] = useState(emptyEmbedding);
  const create = useMutation({
    mutationFn: () =>
      api<WorkspaceDto>('/workspaces', {
        method: 'POST',
        body: {
          name,
          description,
          embedding: toEmbedding(embedding),
        } satisfies CreateWorkspaceRequest,
      }),
    onSuccess: (w) => {
      toast.success(`Workspace ${w.name} created`);
      void queryClient.invalidateQueries({ queryKey: ['workspaces', tenantId] });
      setOpen(false);
      setName('');
      setDescription('');
      setEmbedding(emptyEmbedding());
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) create.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>New workspace</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Create a knowledge workspace</DialogTitle>
          <DialogDescription>
            Agents with this workspace attached can search and read its documents. The description
            tells the model when to search it.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          noValidate
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <ErrorAlert error={create.error} />
          <div className="grid gap-1.5">
            <Label htmlFor="workspace-name">Name</Label>
            <Input
              id="workspace-name"
              className="font-mono"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="support-kb"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="workspace-description">Description</Label>
            <Input
              id="workspace-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Refund, shipping and warranty policies"
            />
          </div>
          <EmbeddingFields
            idPrefix="new-workspace"
            value={embedding}
            onChange={setEmbedding}
            providers={providers}
          />
          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>
              Create workspace
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReindexDialog({ tenantId, workspace }: { tenantId: string; workspace: WorkspaceDto }) {
  const queryClient = useQueryClient();
  const providers = useEmbeddingProviders(tenantId);
  const [open, setOpen] = useState(false);
  const [embedding, setEmbedding] = useState(() => emptyEmbedding(workspace.embedding));
  const reindex = useMutation({
    mutationFn: () =>
      api<WorkspaceDto>(`/workspaces/${workspace.id}/reindex`, {
        method: 'POST',
        body: { embedding: toEmbedding(embedding) },
      }),
    onSuccess: () => {
      toast.success('Re-indexing all documents');
      void queryClient.invalidateQueries({ queryKey: ['workspaces', tenantId] });
      void queryClient.invalidateQueries({
        queryKey: ['workspace-documents', tenantId, workspace.id],
      });
      setOpen(false);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setEmbedding(emptyEmbedding(workspace.embedding));
        else reindex.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <RefreshCw aria-hidden />
          Re-index with new settings
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Re-index {workspace.name}</DialogTitle>
          <DialogDescription>
            Every document is chunked and embedded again with these settings. Search stays available
            meanwhile with the documents that are ready.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          noValidate
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            reindex.mutate();
          }}
        >
          <ErrorAlert error={reindex.error} />
          <EmbeddingFields
            idPrefix="reindex-workspace"
            value={embedding}
            onChange={setEmbedding}
            providers={providers}
          />
          <DialogFooter>
            <Button type="submit" disabled={reindex.isPending}>
              Re-index
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PasteTextDialog({ tenantId, workspaceId }: { tenantId: string; workspaceId: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const add = useMutation({
    mutationFn: () =>
      api<WorkspaceDocumentDto>(`/workspaces/${workspaceId}/documents/text`, {
        method: 'POST',
        body: { name, text },
      }),
    onSuccess: (d) => {
      toast.success(`Added ${d.name}`);
      void queryClient.invalidateQueries({
        queryKey: ['workspace-documents', tenantId, workspaceId],
      });
      void queryClient.invalidateQueries({ queryKey: ['workspaces', tenantId] });
      setOpen(false);
      setName('');
      setText('');
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) add.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <ClipboardPaste aria-hidden />
          Paste text
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Paste text</DialogTitle>
          <DialogDescription>
            Saved as a document; Markdown headings guide chunking.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          noValidate
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <ErrorAlert error={add.error} />
          <div className="grid gap-1.5">
            <Label htmlFor="paste-name">Document name</Label>
            <Input
              id="paste-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="faq.md"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="paste-text">Text</Label>
            <Textarea
              id="paste-text"
              className="min-h-48"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={add.isPending}>
              Add document
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_VARIANT: Record<WorkspaceDocumentStatus, 'secondary' | 'outline' | 'destructive'> = {
  pending: 'outline',
  indexing: 'outline',
  ready: 'secondary',
  failed: 'destructive',
};

function StatusBadge({ doc }: { doc: WorkspaceDocumentDto }) {
  const badge = <Badge variant={STATUS_VARIANT[doc.status]}>{doc.status}</Badge>;
  if (!doc.error) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0}>{badge}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{doc.error}</TooltipContent>
    </Tooltip>
  );
}

function TestSearch({ workspaceId }: { workspaceId: string }) {
  const [query, setQuery] = useState('');
  const search = useMutation({
    mutationFn: () =>
      api<WorkspaceSearchResult>(`/workspaces/${workspaceId}/search`, {
        method: 'POST',
        body: { query, limit: 5 },
      }),
  });
  return (
    <section className="grid gap-3">
      <h3 className="text-sm font-medium">Test search</h3>
      <form
        className="flex gap-2"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (query.trim()) search.mutate();
        }}
      >
        <Input
          aria-label="Search query"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="What the agent might look for"
        />
        <Button type="submit" variant="outline" disabled={search.isPending}>
          Search
        </Button>
      </form>
      <ErrorAlert error={search.error} />
      {search.data && (
        <div className="grid gap-2">
          {search.data.notes.map((n) => (
            <p key={n} className="text-muted-foreground text-xs">
              {n}
            </p>
          ))}
          {search.data.hits.length === 0 ? (
            <p className="text-muted-foreground text-sm">No matching passages.</p>
          ) : (
            search.data.hits.map((h) => (
              <article
                key={h.chunkId}
                data-testid="search-hit"
                className="grid gap-1 rounded-md border p-3"
              >
                <div className="flex items-baseline justify-between gap-2 text-xs">
                  <span className="font-mono font-medium">{h.documentName}</span>
                  <span className="text-muted-foreground tabular-nums">
                    {search.data.mode} · {h.score.toFixed(3)}
                  </span>
                </div>
                <p className="text-muted-foreground line-clamp-4 text-sm whitespace-pre-wrap">
                  {h.text}
                </p>
              </article>
            ))
          )}
        </div>
      )}
    </section>
  );
}

function WorkspaceSheet({
  tenantId,
  workspace,
  canEdit,
  onClose,
}: {
  tenantId: string;
  workspace: WorkspaceDto | null;
  canEdit: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const id = workspace?.id ?? '';
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);
  const docs = useQuery({
    queryKey: ['workspace-documents', tenantId, id],
    queryFn: () => api<{ items: WorkspaceDocumentDto[] }>(`/workspaces/${id}/documents`),
    enabled: Boolean(workspace),
    refetchInterval: (q) =>
      q.state.data?.items.some((d) => d.status === 'pending' || d.status === 'indexing')
        ? 3000
        : false,
  });
  const items = docs.data?.items ?? [];

  // Counts on the list follow the documents as they finish indexing.
  const statuses = items.map((d) => d.status).join();
  useEffect(() => {
    if (statuses) void queryClient.invalidateQueries({ queryKey: ['workspaces', tenantId] });
  }, [statuses, tenantId, queryClient]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['workspace-documents', tenantId, id] });
    void queryClient.invalidateQueries({ queryKey: ['workspaces', tenantId] });
  };
  const reindex = useMutation({
    mutationFn: (docId: string) =>
      api<WorkspaceDocumentDto>(`/workspaces/${id}/documents/${docId}/reindex`, {
        method: 'POST',
      }),
    onSuccess: refresh,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const [deleting, setDeleting] = useState<WorkspaceDocumentDto | null>(null);

  const upload = async (files: FileList | null) => {
    const list = [...(files ?? [])];
    if (list.length === 0) return;
    setUploading((n) => n + list.length);
    const controller = new AbortController();
    for (const file of list) {
      try {
        await uploadFile<WorkspaceDocumentDto>(
          file,
          () => {},
          controller.signal,
          `/workspaces/${id}/documents`,
        );
      } catch (e) {
        toast.error(`${file.name}: ${errorMessage(e)}`);
      } finally {
        setUploading((n) => n - 1);
        refresh();
      }
    }
  };

  return (
    <Sheet open={Boolean(workspace)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-3xl">
        <SheetHeader>
          <SheetTitle className="font-mono">{workspace?.name}</SheetTitle>
          <SheetDescription>{workspace?.description || 'No description'}</SheetDescription>
        </SheetHeader>
        {workspace && (
          <div className="grid gap-6 px-4 pb-6">
            <section className="flex flex-wrap items-center justify-between gap-3 text-sm">
              <span className="text-muted-foreground">
                {workspace.embedding
                  ? `Keyword + vector · ${workspace.embedding.model} · ${workspace.embedding.dims} dims`
                  : 'Keyword only'}
              </span>
              {canEdit && workspace.embeddingLocked && (
                <ReindexDialog tenantId={tenantId} workspace={workspace} />
              )}
            </section>

            <section className="grid gap-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-medium">Documents</h3>
                {canEdit && (
                  <div className="flex gap-2">
                    <input
                      ref={fileInput}
                      type="file"
                      multiple
                      accept={ACCEPT}
                      aria-label="Upload documents"
                      className="sr-only"
                      onChange={(e) => {
                        void upload(e.target.files);
                        e.target.value = '';
                      }}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={uploading > 0}
                      onClick={() => fileInput.current?.click()}
                    >
                      <Upload aria-hidden />
                      {uploading > 0 ? `Uploading ${uploading}…` : 'Upload'}
                    </Button>
                    <PasteTextDialog tenantId={tenantId} workspaceId={id} />
                  </div>
                )}
              </div>
              <ErrorAlert error={docs.error} />
              {!docs.isPending && items.length === 0 ? (
                <EmptyState title="No documents yet">
                  Upload text, Markdown, CSV, JSON, HTML or PDF files (up to 10 MiB each).
                </EmptyState>
              ) : (
                items.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Chunks</TableHead>
                        <TableHead className="w-0">
                          <span className="sr-only">Actions</span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((d) => (
                        <TableRow key={d.id}>
                          <TableCell className="font-mono">
                            <span className="block max-w-56 truncate" title={d.name}>
                              {d.name}
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-xs">
                            {d.mediaType}
                          </TableCell>
                          <TableCell className="text-muted-foreground tabular-nums">
                            {formatBytes(d.sizeBytes)}
                          </TableCell>
                          <TableCell>
                            <StatusBadge doc={d} />
                          </TableCell>
                          <TableCell className="text-muted-foreground tabular-nums">
                            {d.chunkCount}
                          </TableCell>
                          <TableCell>
                            {canEdit && (
                              <div className="flex justify-end gap-1">
                                <Button
                                  size="icon-sm"
                                  variant="ghost"
                                  aria-label={`Re-index ${d.name}`}
                                  title="Re-index"
                                  disabled={reindex.isPending && reindex.variables === d.id}
                                  onClick={() => reindex.mutate(d.id)}
                                >
                                  <RefreshCw aria-hidden />
                                </Button>
                                <Button
                                  size="icon-sm"
                                  variant="ghost-destructive"
                                  aria-label={`Delete ${d.name}`}
                                  title="Delete"
                                  onClick={() => setDeleting(d)}
                                >
                                  <Trash2 aria-hidden />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )
              )}
            </section>

            <TestSearch workspaceId={id} />
          </div>
        )}
        <ConfirmDeleteDialog
          open={deleting !== null}
          onOpenChange={(open) => !open && setDeleting(null)}
          title={`Delete ${deleting?.name ?? 'document'}?`}
          description="Its passages are removed from search right away."
          onConfirm={async () => {
            await api<void>(`/workspaces/${id}/documents/${deleting?.id}`, { method: 'DELETE' });
            refresh();
          }}
        />
      </SheetContent>
    </Sheet>
  );
}

function WorkspacesContent({ tenantId, role }: { tenantId: string; role: Role }) {
  const canEdit = hasRole(role, 'admin');
  const queryClient = useQueryClient();
  const workspaces = useQuery({
    queryKey: ['workspaces', tenantId],
    queryFn: () => api<{ items: WorkspaceDto[] }>('/workspaces'),
  });
  const items = workspaces.data?.items ?? [];
  const search = useListSearch(items, (w) => [w.name, w.description]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<WorkspaceDto | null>(null);
  const open = items.find((w) => w.id === openId) ?? null;

  return (
    <>
      <PageHeader
        title="Knowledge"
        description="Document workspaces that agents search and read when a question needs them."
      />
      <div className="grid gap-4">
        <ListSearch
          label="Search workspaces"
          value={search.query}
          onChange={search.setQuery}
          actions={canEdit && <NewWorkspaceDialog tenantId={tenantId} />}
        />
        <ErrorAlert error={workspaces.error} />
        {!workspaces.isPending && items.length === 0 ? (
          <EmptyState title="No knowledge workspaces yet">
            Create one, add documents, then attach it to an agent in the agent editor.
          </EmptyState>
        ) : items.length > 0 && search.filtered.length === 0 ? (
          <NoMatches query={search.query} />
        ) : (
          items.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Search</TableHead>
                  <TableHead>Documents</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="w-0">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {search.filtered.map((w) => (
                  <TableRow key={w.id}>
                    <TableCell className="font-mono font-medium">{w.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      <span className="block max-w-sm truncate" title={w.description || undefined}>
                        {w.description || '—'}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {w.embedding ? 'Keyword + vector' : 'Keyword'}
                    </TableCell>
                    <TableCell className="text-muted-foreground tabular-nums">
                      {w.readyCount === w.documentCount
                        ? w.documentCount
                        : `${w.readyCount} of ${w.documentCount} ready`}
                    </TableCell>
                    <TableCell className="text-muted-foreground tabular-nums">
                      {formatBytes(w.totalBytes)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(w.updatedAt)}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          aria-label={`Open ${w.name}`}
                          onClick={() => setOpenId(w.id)}
                        >
                          Open
                        </Button>
                        {canEdit && (
                          <Button
                            size="icon-sm"
                            variant="ghost-destructive"
                            aria-label={`Delete ${w.name}`}
                            title="Delete"
                            onClick={() => setDeleting(w)}
                          >
                            <Trash2 aria-hidden />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        )}
      </div>
      <WorkspaceSheet
        tenantId={tenantId}
        workspace={open}
        canEdit={canEdit}
        onClose={() => setOpenId(null)}
      />
      <ConfirmDeleteDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`Delete ${deleting?.name ?? 'workspace'}?`}
        description="All of its documents are deleted. Detach it from agents first."
        onConfirm={async () => {
          await api<void>(`/workspaces/${deleting?.id}`, { method: 'DELETE' });
          void queryClient.invalidateQueries({ queryKey: ['workspaces', tenantId] });
        }}
      />
    </>
  );
}

export function WorkspacesPage() {
  return (
    <RequireTenant>
      {(tenant) => <WorkspacesContent tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}
